import { devLog, devError } from '../utils/devLogger';
import { usePageTitle } from '../hooks/usePageTitle';
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button, Modal, Form, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import { db } from '../firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import AdminNavbar from '../components/AdminNavbar';
import '../styles/brand.css';

const Notifications = () => {
  usePageTitle("Notifications");
  const { currentUser, userProfile } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingNotification, setEditingNotification] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'announcement',
    priority: 'normal',
    targetUsers: [],
    targetRoles: ['all'],
    status: 'draft',
    scheduledFor: ''
  });
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });

  useEffect(() => {
    if (currentUser && userProfile?.role === 'admin') {
      loadData();
    }
  }, [currentUser, userProfile]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load notifications
      const notificationsCollection = collection(db, 'notifications');
      const notificationsQuery = query(notificationsCollection, orderBy('createdAt', 'desc'));
      const notificationsSnapshot = await getDocs(notificationsQuery);
      const notificationsData = notificationsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt || Date.now()),
        scheduledFor: doc.data().scheduledFor?.toDate?.() || new Date(doc.data().scheduledFor || Date.now()),
        sentAt: doc.data().sentAt?.toDate?.() || null
      }));
      
      // Load users for targeting
      const usersCollection = collection(db, 'users');
      const usersSnapshot = await getDocs(usersCollection);
      const usersData = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setNotifications(notificationsData);
      setUsers(usersData);
      
    } catch (error) {
      devError('Error loading data:', error);
      showAlert('Error loading notifications', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (message, type) => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: '', type: '' }), 5000);
  };

  const handleShowModal = (notification = null) => {
    if (notification) {
      setEditingNotification(notification);
      setFormData({
        title: notification.title,
        message: notification.message,
        type: notification.type || 'announcement',
        priority: notification.priority || 'normal',
        targetUsers: notification.targetUsers || [],
        targetRoles: notification.targetRoles || ['all'],
        status: notification.status || 'draft',
        scheduledFor: notification.scheduledFor instanceof Date && !isNaN(notification.scheduledFor) ? notification.scheduledFor.toISOString().slice(0, 16) : ''
      });
    } else {
      setEditingNotification(null);
      setFormData({
        title: '',
        message: '',
        type: 'announcement',
        priority: 'normal',
        targetUsers: [],
        targetRoles: ['all'],
        status: 'draft',
        scheduledFor: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingNotification(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRoleChange = (e) => {
    const { value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      targetRoles: checked 
        ? [...prev.targetRoles, value]
        : prev.targetRoles.filter(role => role !== value)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const notificationData = {
        ...formData,
        scheduledFor: formData.scheduledFor ? new Date(formData.scheduledFor) : new Date(),
        updatedAt: serverTimestamp()
      };

      if (editingNotification) {
        await updateDoc(doc(db, 'notifications', editingNotification.id), notificationData);
        showAlert('Notification updated successfully!', 'success');
      } else {
        await addDoc(collection(db, 'notifications'), {
          ...notificationData,
          createdAt: serverTimestamp(),
          createdBy: currentUser.uid,
          deliveredCount: 0,
          readCount: 0,
          sentAt: null
        });
        showAlert('Notification created successfully!', 'success');
      }
      
      handleCloseModal();
      loadData();
      
    } catch (error) {
      devError('Error saving notification:', error);
      showAlert('Error saving notification', 'danger');
    }
  };

  const handleDelete = async (notificationId) => {
    if (window.confirm('Are you sure you want to delete this notification? This action cannot be undone.')) {
      try {
        await deleteDoc(doc(db, 'notifications', notificationId));
        showAlert('Notification deleted successfully!', 'success');
        loadData();
      } catch (error) {
        devError('Error deleting notification:', error);
        showAlert('Error deleting notification', 'danger');
      }
    }
  };

  const handleSend = async (notificationId) => {
    if (window.confirm('Are you sure you want to send this notification now?')) {
      try {
        await updateDoc(doc(db, 'notifications', notificationId), {
          status: 'sent',
          sentAt: serverTimestamp(),
          scheduledFor: serverTimestamp()
        });
        showAlert('Notification sent successfully!', 'success');
        loadData();
      } catch (error) {
        devError('Error sending notification:', error);
        showAlert('Error sending notification', 'danger');
      }
    }
  };

  const getStatusBadge = (status, scheduledFor) => {
    if (status === 'sent') return <Badge bg="success">Sent</Badge>;
    if (status === 'scheduled' && scheduledFor > new Date()) return <Badge bg="info">Scheduled</Badge>;
    if (status === 'scheduled' && scheduledFor <= new Date()) return <Badge bg="warning">Pending</Badge>;
    return <Badge bg="secondary">Draft</Badge>;
  };

  const getTypeBadge = (type) => {
    const variants = {
      'announcement': 'primary',
      'reminder': 'warning',
      'alert': 'danger',
      'update': 'info'
    };
    return <Badge bg={variants[type] || 'primary'}>{type}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const variants = {
      'low': 'success',
      'normal': 'info',
      'high': 'warning',
      'urgent': 'danger'
    };
    return <Badge bg={variants[priority] || 'info'}>{priority}</Badge>;
  };

  if (userProfile?.role !== 'admin') {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <h4>Access Denied</h4>
          <p>You do not have permission to access notification management.</p>
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <>
        <AdminNavbar currentPage="notifications" />
        <Container className="mt-4">
          <div className="text-center my-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-2">Loading notifications...</p>
          </div>
        </Container>
      </>
    );
  }

  const sentNotifications = notifications.filter(n => n.status === 'sent');
  const scheduledNotifications = notifications.filter(n => n.status === 'scheduled');
  const draftNotifications = notifications.filter(n => n.status === 'draft');

  return (
    <>
      <AdminNavbar currentPage="notifications" />
      
      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row>
            <Col lg={8}>
              <h1 className="mb-3">Notification Management</h1>
              <p className="mission-text mb-4">
                Configure and manage system notifications
              </p>
              <p className="mb-0">
                Create, schedule, and manage push notifications to keep users engaged and informed about important updates, reminders, and system announcements across the platform.
              </p>
            </Col>
            <Col lg={4} className="d-flex align-items-center justify-content-end">
              <Button variant="outline-light" onClick={() => handleShowModal()}>
                ➕ Create Notification
              </Button>
            </Col>
          </Row>
        </Container>
      </section>

      <Container className="mt-4">

        {alert.show && (
          <Alert variant={alert.type} className="mb-4">
            {alert.message}
          </Alert>
        )}

        {/* Notification Statistics */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-primary">{notifications.length}</h3>
                <small className="text-muted">Total Notifications</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-success">{sentNotifications.length}</h3>
                <small className="text-muted">Sent</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-info">{scheduledNotifications.length}</h3>
                <small className="text-muted">Scheduled</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-warning">{draftNotifications.length}</h3>
                <small className="text-muted">Drafts</small>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Notifications Table */}
        <Card>
          <Card.Header>
            <h5>All Notifications</h5>
          </Card.Header>
          <Card.Body>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Notification</th>
                  <th>Type</th>
                  <th>Priority</th>
                  <th>Target</th>
                  <th>Status</th>
                  <th>Scheduled</th>
                  <th>Delivered</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {notifications.map((notification) => (
                  <tr key={notification.id}>
                    <td>
                      <strong>{notification.title}</strong>
                      <div><small className="text-muted">{notification.message?.substring(0, 80)}{notification.message?.length > 80 ? '...' : ''}</small></div>
                    </td>
                    <td>{getTypeBadge(notification.type)}</td>
                    <td>{getPriorityBadge(notification.priority)}</td>
                    <td>
                      {notification.targetRoles?.includes('all') ? (
                        <Badge bg="primary">All Users</Badge>
                      ) : (
                        <div>
                          {notification.targetRoles?.map(role => (
                            <Badge key={role} bg="info" className="me-1">{role}</Badge>
                          ))}
                          {notification.targetUsers?.length > 0 && (
                            <div><small>{notification.targetUsers.length} specific users</small></div>
                          )}
                        </div>
                      )}
                    </td>
                    <td>{getStatusBadge(notification.status, notification.scheduledFor)}</td>
                    <td>
                      <small>
                        {notification.scheduledFor instanceof Date && !isNaN(notification.scheduledFor)
                          ? `${notification.scheduledFor.toLocaleDateString()} ${notification.scheduledFor.toLocaleTimeString()}`
                          : '—'}
                      </small>
                    </td>
                    <td>
                      <Badge bg="success">{notification.deliveredCount || 0}</Badge>
                    </td>
                    <td>
                      <Button 
                        variant="outline-primary" 
                        size="sm" 
                        className="me-1"
                        onClick={() => handleShowModal(notification)}
                      >
                        Edit
                      </Button>
                      {notification.status !== 'sent' && (
                        <Button 
                          variant="outline-success" 
                          size="sm"
                          className="me-1"
                          onClick={() => handleSend(notification.id)}
                        >
                          Send Now
                        </Button>
                      )}
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        onClick={() => handleDelete(notification.id)}
                        disabled={notification.status === 'sent'}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
                {notifications.length === 0 && (
                  <tr>
                    <td colSpan="8" className="text-center text-muted py-4">
                      No notifications found. Create your first notification to get started.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        {/* Add/Edit Notification Modal */}
        <Modal show={showModal} onHide={handleCloseModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              {editingNotification ? 'Edit Notification' : 'Create New Notification'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Notification Title *</Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., Assignment Due Reminder"
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Message *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter the notification message..."
                />
              </Form.Group>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Type *</Form.Label>
                    <Form.Select
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="announcement">Announcement</option>
                      <option value="reminder">Reminder</option>
                      <option value="alert">Alert</option>
                      <option value="update">Update</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Priority *</Form.Label>
                    <Form.Select
                      name="priority"
                      value={formData.priority}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Status *</Form.Label>
                    <Form.Select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="draft">Draft</option>
                      <option value="scheduled">Scheduled</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Target Audience *</Form.Label>
                <div>
                  <Form.Check
                    type="checkbox"
                    label="All Users"
                    value="all"
                    checked={formData.targetRoles.includes('all')}
                    onChange={handleRoleChange}
                  />
                  <Form.Check
                    type="checkbox"
                    label="Learners"
                    value="learner"
                    checked={formData.targetRoles.includes('learner')}
                    onChange={handleRoleChange}
                  />
                  <Form.Check
                    type="checkbox"
                    label="Facilitators"
                    value="facilitator"
                    checked={formData.targetRoles.includes('facilitator')}
                    onChange={handleRoleChange}
                  />
                  <Form.Check
                    type="checkbox"
                    label="Admins"
                    value="admin"
                    checked={formData.targetRoles.includes('admin')}
                    onChange={handleRoleChange}
                  />
                </div>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Schedule For *</Form.Label>
                <Form.Control
                  type="datetime-local"
                  name="scheduledFor"
                  value={formData.scheduledFor}
                  onChange={handleInputChange}
                  required
                />
                <Form.Text className="text-muted">
                  When should this notification be sent?
                </Form.Text>
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                {editingNotification ? 'Update Notification' : 'Create Notification'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </>
  );
};

export default Notifications;